import { useCallback, useState } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";

import Profile from "./components/Profile";
import ConfirmEmailUpdate from "./components/ConfirmEmailUpdate";
import NotFound from "../../app/components/NotFound";
import ProtectedRoute from "../../app/components/ProtectedRoute";

import EditEmailModal from "./components/EditEmailModal";
import EditPasswordModal from "./components/EditPasswordModal";

import api from "../../shared/utils/api";
import {
  EMAIL_UPDATED_SUCCESSFULLY_MSG,
  EMAIL_UPDATE_REQUEST_ERROR_MSG,
  PASSWORD_UPDATED_SUCCESSFULLY_MSG,
  PASSWORD_UPDATE_ERROR_MSG,
  UPDATE_EMAIL_EMAIL_SENT_MSG,
} from "../../shared/utils/messages";

function ProfileRoot({
  loggedIn,
  isAuthInitialized,
  clearSession,
  isLoading,
  startLoading,
  stopLoading,
  openModal,
  onMenuClick,
  handleSignout,
  isMenuOpen,
  onContactClick,
}) {
  const [isEditEmailModalOpen, setIsEditEmailModalOpen] = useState(false);
  const [isEditPasswordModalOpen, setIsEditPasswordModalOpen] = useState(false);
  const navigate = useNavigate();

  const closeProfilePopups = useCallback(() => {
    setIsEditEmailModalOpen(false);
    setIsEditPasswordModalOpen(false);
  }, []);

  function handleEditEmailBtnClick() {
    setIsEditEmailModalOpen(!isEditEmailModalOpen);
  }

  function handleEmailChangeRequest({ email, currentPassword }) {
    startLoading();
    api
      .requestEmailUpdate(email, currentPassword)
      .then(() => {
        closeProfilePopups();
        openModal({
          status: "success",
          message: UPDATE_EMAIL_EMAIL_SENT_MSG,
        });
      })
      .catch((err) => {
        openModal({
          status: "error",
          message: err.message || EMAIL_UPDATE_REQUEST_ERROR_MSG,
        });
      })
      .finally(() => stopLoading());
  }

  async function handleUpdateEmail(token) {
    const result = await api.updateEmail(token);
    if (result.completed) {
      closeProfilePopups();
      clearSession();
      navigate("/signin", { replace: true });
      openModal({
        status: "success",
        message: EMAIL_UPDATED_SUCCESSFULLY_MSG,
      });
    }
    return result;
  }

  function handleEditPasswordBtnClick() {
    setIsEditPasswordModalOpen(!isEditPasswordModalOpen);
  }

  function handleUpdatePassword(passwordData) {
    startLoading();

    api
      .updatePassword(passwordData)
      .then((data) => {
        closeProfilePopups();
        openModal({
          status: "success",
          message: data.message || PASSWORD_UPDATED_SUCCESSFULLY_MSG,
        });
      })
      .catch((err) => {
        openModal({
          status: "error",
          message: err.message || PASSWORD_UPDATE_ERROR_MSG,
        });
      })
      .finally(() => stopLoading());
  }

  return (
    <>
      <Routes>
        <Route
          index
          element={
            <ProtectedRoute
              loggedIn={loggedIn}
              isAuthInitialized={isAuthInitialized}
            >
              <Profile
                loggedIn={loggedIn}
                onEditEmailBtnClick={handleEditEmailBtnClick}
                onEditPasswordBtnClick={handleEditPasswordBtnClick}
                onMenuClick={onMenuClick}
                onSignout={handleSignout}
                isSendingReq={isLoading}
                onLogout={handleSignout}
                isMenuOpen={isMenuOpen}
                menuId="app-main-menu"
                onContactClick={onContactClick}
              />
            </ProtectedRoute>
          }
        />

        <Route
          path="update-email/:token"
          element={
            <ProtectedRoute
              loggedIn={loggedIn}
              isAuthInitialized={isAuthInitialized}
            >
              <ConfirmEmailUpdate
                loggedIn={loggedIn}
                onLogout={handleSignout}
                onMenuClick={onMenuClick}
                isMenuOpen={isMenuOpen}
                menuId="app-main-menu"
                onUpdateEmail={handleUpdateEmail}
                onContactClick={onContactClick}
              />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Routes>

      <EditEmailModal
        isOpen={isEditEmailModalOpen}
        onClose={closeProfilePopups}
        isSendingReq={isLoading}
        onRequestEmailChange={handleEmailChangeRequest}
      />

      <EditPasswordModal
        isOpen={isEditPasswordModalOpen}
        onClose={closeProfilePopups}
        isSendingReq={isLoading}
        onUpdatePassword={handleUpdatePassword}
      />
    </>
  );
}

export default ProfileRoot;
